import { useState, useEffect, useCallback } from 'react';
import { API_URL } from './constants';

const STORAGE_KEY = 'forloop_auth_state';
const SESSION_RENEWAL_KEY = 'auth0_backend_renewal_attempted';

/**
 * If the backend session has expired but the user previously authenticated
 * with auth0-backend, redirect to login so Auth0 can silently renew the
 * session (Auth0 recognises the user and skips the login form).
 * A sessionStorage flag prevents infinite redirect loops in case Auth0
 * itself also has an expired session.
 */
const attemptSilentSessionRenewal = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    const authState = JSON.parse(stored);
    if (authState?.authType !== 'auth0-backend') return;

    // Guard against infinite loops
    if (sessionStorage.getItem(SESSION_RENEWAL_KEY)) return;
    sessionStorage.setItem(SESSION_RENEWAL_KEY, '1');

    window.location.href = `${API_URL}/api/auth/login`;
  } catch (_) {
    // Ignore storage errors
  }
};

/**
 * Custom hook for Auth0 backend authentication
 * Similar to Next.js @auth0/nextjs-auth0/client useUser hook
 * 
 * Usage:
 * const { user, loading, error, login, logout, refreshUser } = useAuth0Backend();
 */
export const useAuth0Backend = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication status (best-effort; failures must not block Login redirect — CORS/network often breaks fetch with credentials cross-origin)
  const checkAuth = useCallback(async () => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/check`, {
        credentials: 'include', // Include cookies
      });

      if (!response.ok) {
        setUser(null);
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.authenticated) {
        // Clear renewal flag on successful authentication
        sessionStorage.removeItem(SESSION_RENEWAL_KEY);
        setUser(data.user);
      } else {
        setUser(null);
        // Session expired – try to silently renew if previously auth0-backend
        attemptSilentSessionRenewal();
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setUser(null);
      // Do not set `error` here: check is optional; Login still works via full-page navigation to /api/auth/login.
    } finally {
      setLoading(false);
    }
  }, []);

  // Get user info
  const getUser = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/user`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to get user');
      }

      const data = await response.json();
      setUser(data.user);
      return data.user;
    } catch (err) {
      console.error('Get user error:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Refresh user and session heartbeat
  // Similar to Next.js refreshUserAndSessionHeartbeat
  const refreshUser = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        setUser(null);
        // Session expired during heartbeat – attempt silent renewal
        attemptSilentSessionRenewal();
        throw new Error('Failed to refresh session');
      }

      const data = await response.json();
      sessionStorage.removeItem(SESSION_RENEWAL_KEY);
      setUser(data.user);
      return data;
    } catch (err) {
      console.error('Refresh session error:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Login - redirect to backend auth endpoint
  const login = useCallback(() => {
    window.location.href = `${API_URL}/api/auth/login`;
  }, []);

  // Logout
  const logout = useCallback(() => {
    // Clear local user state first
    setUser(null);
    setError(null);
    
    // Redirect to backend logout endpoint which will handle Auth0 logout
    // Using direct navigation instead of fetch because the endpoint returns a redirect
    window.location.href = `${API_URL}/api/auth/logout`;
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Refresh user session periodically (every 5 minutes)
  useEffect(() => {
    if (user) {
      const interval = setInterval(() => {
        refreshUser();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [user, refreshUser]);

  return {
    user,
    loading,
    error,
    login,
    logout,
    refreshUser,
    getUser,
    setUserData: setUser,
    isAuthenticated: !!user,
  };
};

/**
 * Hook similar to Next.js useRefreshUserAndSession
 * 
 * Usage:
 * const [state] = useRefreshUserAndSession();
 */
export const useRefreshUserAndSession = () => {
  const { user, refreshUser } = useAuth0Backend();
  const [state, setState] = useState('loading');

  const fetchUserAndSessionHeartbeat = useCallback(async () => {
    if (user && user.email) {
      try {
        await refreshUser();
        setState('success');
      } catch (e) {
        setState('error');
      }
    }
  }, [user, refreshUser]);

  useEffect(() => {
    fetchUserAndSessionHeartbeat();
  }, [fetchUserAndSessionHeartbeat]);

  return [state];
};

